import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { loginRequest, logoutRequest, meRequest } from './api';
import { useAuthStore } from './store';
import { isMfaChallenge } from './types';
import type { LoginInput, LoginSession, MemberType } from './types';

/**
 * Role-based home page. Chambers staff go to /admin, /clerk, or /barrister.
 * External solicitors land on /solicitor — a completely separate portal.
 *
 * `staff` is listed EXPLICITLY rather than left to the default. Falling through sent a staff
 * member to /barrister, which `BARRISTERS` does not admit (App.tsx), so ProtectedRoute bounced
 * them back to routeForMember('staff') — /barrister again — and the redirect never terminated:
 * a staff member could not reach any page at all. /settings is the role-agnostic home App.tsx
 * already documents for members with no role-scoped area of their own, it is guarded by a plain
 * ProtectedRoute with no member-type list (so it cannot bounce), and `settingsBasePath('staff')`
 * already resolves there (features/settings/manifest.ts).
 */
export function routeForMember(memberType: MemberType): string {
  switch (memberType) {
    case 'admin':
      return '/admin';
    case 'senior_clerk':
    case 'junior_clerk':
      return '/clerk';
    case 'solicitor':
      return '/solicitor';
    case 'staff':
      return '/settings';
    default:
      return '/barrister';
  }
}

/**
 * Establish a session and go where every authentication method goes.
 *
 * Shared by the password path and the post-TOTP path so that the two CANNOT diverge — one
 * authentication architecture, not three (docs/SESSION_BOOTSTRAP.md). When MFA landed, the
 * second factor being a different code path is exactly how it would have ended up skipping the
 * bootstrap resolver.
 */
export function useEstablishSession() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  return (session: LoginSession) => {
    setSession(session);
    toast.success(`Welcome back, ${session.user.firstName}`);
    navigate('/post-login?intent=sign_in', { replace: true });
  };
}

/**
 * The password step.
 *
 * A correct password yields ONE OF TWO THINGS, and this hook must handle both. Previously it
 * handled one: it passed the response straight to `setSession` and read `data.user.firstName`.
 * For an account with TOTP enabled the response carries no user, so signing in threw on
 * `undefined` and left the person on the login page with a session that was never created —
 * turning MFA on removed your own ability to sign in.
 *
 * `onMfaRequired` is how the challenge reaches the page. It is a required argument rather than
 * an optional callback on purpose: a caller that forgets it does not compile.
 */
export function useLogin(onMfaRequired: (challengeToken: string) => void) {
  const establish = useEstablishSession();

  return useMutation({
    mutationFn: (input: LoginInput) => loginRequest(input),
    onSuccess: (data) => {
      if (isMfaChallenge(data)) {
        // No session, no toast, no navigation — the person is not signed in yet. The challenge
        // stays in React state and is never written to localStorage: it is a live credential
        // with a 5-minute life, and persisting it would outlive the tab it belongs to.
        onMfaRequired(data.challengeToken);
        return;
      }
      establish(data);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Login failed');
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => logoutRequest(),
    onSettled: () => {
      clear();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });
}

export function useCurrentUser() {
  const isLoggedIn = useAuthStore((s) => !!s.user);
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => meRequest(),
    enabled: isLoggedIn,
    staleTime: 5 * 60 * 1000,
  });
}
